import { UsersIcon } from 'lucide-react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Avatar,
  EmptyState,
  ErrorNotice,
  Pagination,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SelectInput,
  SkeletonTable,
  Tabs,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { useUrlPage, useUrlSearch } from '@/lib/use-list-query-state';
import { avatarSrc, listUsers, personName } from '@/lib/events';
import { ROLE_QUERY_KEY, canManageRoles, listRoles, useMyIdentity } from '@/lib/roles';
import { ContactsDirectory } from '@/pages/ContactsPage';

/**
 * The consolidated people directory: portal users (staff, facilitators,
 * client logins) and — for internal staff — the CRM client contacts, one
 * surface with type tabs. Users and contacts stay separate records
 * underneath (auth identity vs CRM identity); the tabs are the merge.
 */
const PEOPLE_TABS = ['all', 'internal', 'contractor', 'admin', 'client', 'contacts'] as const;
type PeopleTab = (typeof PEOPLE_TABS)[number];

/** Tab → users-endpoint role filter ('contacts' switches lists entirely). */
const TAB_ROLES: Readonly<Record<Exclude<PeopleTab, 'all' | 'contacts'>, string>> = {
  internal: 'internal',
  contractor: 'contractor',
  admin: 'admin,superadmin',
  client: 'client',
};

/** URL params this page owns beyond page and search. */
const PEOPLE_PARAM = { type: 'type', role: 'role' } as const;

/** A role id as the API accepts it; anything else in the URL is ignored. */
const ROLE_ID_PATTERN = /^[a-f0-9]{24}$/i;

export function PeoplePage(): React.ReactElement {
  // Page and query live in the URL so a browser back returns to the exact
  // page and search the person left — the list is a bookmarkable place, not
  // transient local state.
  const [params, setParams] = useSearchParams();
  const { page, setPage } = useUrlPage();
  const tab = (PEOPLE_TABS as readonly string[]).includes(params.get(PEOPLE_PARAM.type) ?? '')
    ? (params.get(PEOPLE_PARAM.type) as PeopleTab)
    : 'all';
  const identity = useMyIdentity();
  const isInternal = ['internal', 'admin', 'superadmin'].includes(identity.data?.role ?? '');
  // The role filter is part of the Roles rollout: hidden, and never sent,
  // unless RBAC is on and this person manages roles. The API ignores it
  // otherwise too.
  const showRoleFilter = canManageRoles(identity.data);
  const roles = useQuery({
    queryKey: ROLE_QUERY_KEY.list,
    queryFn: ({ signal }) => listRoles(signal),
    enabled: showRoleFilter,
  });
  const roleParam = params.get(PEOPLE_PARAM.role) ?? '';
  const roleId = showRoleFilter && ROLE_ID_PATTERN.test(roleParam) ? roleParam : undefined;
  const selectedRole = roles.data?.find((entry) => entry.id === roleId);

  const { search, setSearch, debounced } = useUrlSearch();

  const role = tab !== 'all' && tab !== 'contacts' ? TAB_ROLES[tab] : undefined;
  const people = useQuery({
    queryKey: ['directory', debounced, page, role, roleId],
    queryFn: ({ signal }) =>
      listUsers(
        {
          page,
          ...(debounced ? { search: debounced } : {}),
          ...(role ? { role } : {}),
          ...(roleId ? { roleId } : {}),
        },
        signal,
      ),
    placeholderData: keepPreviousData,
    // A ?role= link waits for the identity check, so it never flashes the
    // unfiltered list first.
    enabled: tab !== 'contacts' && (roleParam === '' || !identity.isPending),
  });

  const rangeStart = people.data ? (people.data.page - 1) * 25 + 1 : 0;
  const rangeEnd = people.data ? rangeStart + people.data.people.length - 1 : 0;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="PEOPLE"
        title="Directory"
        subtitle="Everyone in bcecloud — facilitators, staff and client contacts."
      />

      <div className="mb-5">
        <Tabs<PeopleTab>
          label="Directory types"
          items={[
            { key: 'all', label: 'Everyone' },
            { key: 'internal', label: 'Internal' },
            { key: 'contractor', label: 'Contractors' },
            { key: 'admin', label: 'Admins' },
            { key: 'client', label: 'Client users' },
            ...(isInternal ? [{ key: 'contacts' as const, label: 'Client contacts' }] : []),
          ]}
          active={tab}
          onChange={(next) =>
            setParams(
              (prev) => {
                const nextParams = new URLSearchParams(prev);
                nextParams.set(PEOPLE_PARAM.type, next);
                nextParams.delete('page');
                return nextParams;
              },
              { replace: true },
            )
          }
        />
      </div>

      {tab === 'contacts' ? (
        <ContactsDirectory />
      ) : (
      <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search people"
          placeholder="Search by name or email"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
          autoFocus
        />
        {showRoleFilter ? (
          <SelectInput
            aria-label="Filter by role"
            value={roleId ?? ''}
            onChange={(event) =>
              setParams(
                (prev) => {
                  const nextParams = new URLSearchParams(prev);
                  if (event.target.value) nextParams.set(PEOPLE_PARAM.role, event.target.value);
                  else nextParams.delete(PEOPLE_PARAM.role);
                  nextParams.delete('page');
                  return nextParams;
                },
                { replace: true },
              )
            }
            className="mt-0 w-auto min-w-52"
          >
            <option value="">All roles</option>
            {roleId !== undefined && roles.data !== undefined && selectedRole === undefined ? (
              <option value={roleId}>Role no longer available</option>
            ) : null}
            {(roles.data ?? []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </SelectInput>
        ) : null}
      </div>

      {people.isError ? (
        <ErrorNotice title="Directory unavailable" message={errorMessage(people.error)} />
      ) : people.isPending ? (
        <SkeletonTable rows={8} columns={['w-[40%]', 'w-[12%]']} label="Loading the directory…" />
      ) : people.data.people.length === 0 ? (
        <Panel>
          {debounced ? (
            <EmptyState message={`Nobody matches “${debounced}”.`} />
          ) : roleId !== undefined ? (
            <EmptyState
              icon={<UsersIcon aria-hidden className="size-6" />}
              title="Nobody holds this role"
              message={`People given ${selectedRole ? `the ${selectedRole.name} role` : 'this role'} will appear here. Assign roles from a person's profile.`}
            />
          ) : (
            <EmptyState
              icon={<UsersIcon aria-hidden className="size-6" />}
              title="No people yet"
              message="Facilitators, staff and client contacts will appear here as they are added."
            />
          )}
        </Panel>
      ) : (
        <>
          <Panel className={people.isFetching ? 'opacity-60 transition-opacity' : undefined}>
            <ul>
              {people.data.people.map((person) => (
                <li key={person._id} className="border-b border-portal-line last:border-b-0">
                  <Link
                    to={`/people/${person._id}${page > 1 ? `?from=${page}` : ''}`}
                    className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-portal-bg"
                  >
                    <Avatar name={personName(person)} size={32} imageUrl={avatarSrc(person)} />
                    <span className="min-w-0 flex-1">
                      <b className="block truncate text-portal-small">{personName(person)}</b>
                      <span className="block truncate text-portal-micro text-portal-muted">
                        {[person.email, person.company].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    {person.role ? (
                      <Pill tone={person.role === 'client' ? 'brand' : 'neutral'}>
                        {person.role}
                      </Pill>
                    ) : null}
                    <span aria-hidden="true" className="text-portal-muted">
                      ›
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </Panel>
          <Pagination
            page={people.data.page}
            totalPages={people.data.totalPages}
            onChange={setPage}
            summary={`${rangeStart}–${rangeEnd} of ${people.data.total}`}
          />
        </>
      )}
      </>
      )}
    </section>
  );
}
